/* eslint-disable no-console */
import { SpanishDatePipe } from '@/app/pipe/date.pipe';
import { EconomicAmountPipe } from '@/app/pipe/economicAmount.pipe';
import { mapSystemStateInfo } from '@/app/services/ia-services/system-state.mapper';
import { SystemsService } from '@/app/services/ia-services/systems.service';
import { BackButtonComponent } from '@/app/shared/back-button/back-button.component';
import { ProjectCardComponent } from '@/app/shared/cards/project-card/project-card.component';
import { PillComponent } from '@/app/shared/pill/pill.component';
import { CommonModule } from '@angular/common';
import { Component, inject, signal, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DesyAngularModule } from 'desy-angular';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { RiskLevelClassPipe } from '../../../../pipe/riskLevelClass.pipe';
import { mapSystemTypeLabel } from '@/app/shared/mappers/system-type-label.mapper';
import { getTaskFunctionLabel as mapTaskFunctionLabel } from '@/app/shared/mappers/task-function-label.mapper';
import { mapRiskLevelLabel } from '@/app/shared/mappers/risk-level-label.mapper';
import { ReviewStatesComponent } from '@/app/shared/review-states/review-states.component';
import { environment } from '@/environments/environment';
import {
  SystemReviewDetail,
  SystemReviewRole,
  SystemReviewStage,
  SystemReviewUserData,
} from '@/app/interfaces/system-review';
import { SystemCorrectionRole } from '@/app/interfaces/system-correction';

@Component({
  selector: 'app-system-of-ia-details',
  imports: [
    CommonModule,
    DesyAngularModule,
    BackButtonComponent,
    ProjectCardComponent,
    PillComponent,
    SpanishDatePipe,
    EconomicAmountPipe,
    ButtonsComponent,
    RiskLevelClassPipe,
    ReviewStatesComponent,
  ],
  templateUrl: './system-of-ia-details.component.html',
  styleUrl: './system-of-ia-details.component.scss',
})
export class SystemOfIaDetailsComponent implements OnInit {
  private systemsService = inject(SystemsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  id_system: string = '';
  userRoles: SystemReviewRole[] = [];
  stateGroups: { group: SystemReviewStage; state: string }[] = [];

  cardSystem = signal<SystemReviewDetail | null>(null);
  loading = signal(true);
  systemDetail = signal<SystemReviewDetail | null>(null);
  stateTextFormat = signal('');
  colorBox = signal('');

  readonly projectCardFallbackDate = new Date();

  contractValidityDate = signal('--/--/----');

  deployButton = {
    url: null,
    label: 'Desplegar sistema de IA',
  };

  async ngOnInit() {
    this.id_system = this.route.snapshot.paramMap.get('id') ?? '';
    const data = (await this.systemsService.getSystemDetails(this.id_system, 'system')) as SystemReviewDetail;
    data.infoCard?.forEach((card) => {
      card.buttons = [{ label: 'Consultar detalles', url: 'catalogo-ia/proyectos/detalles/' + card.id }];
    });
    this.cardSystem.set(data);
    this.systemDetail.set(data);
    this.setStateInfo(data.state);
    this.stateGroups = this.buildStateGroups(data);
    this.userRoles = this.getUserRolesForSystem(data);
    this.loading.set(false);
    console.log('Detalles del sistema: ', this.systemDetail());
    this.deployButton.url = 'catalogo-ia/sistemas-de-ia/' + this.id_system + '/desplegar-sistema';

    if (this.systemDetail()?.contractValidityDate) {
      const contractValidityString = this.systemDetail()?.contractValidityDate.toString() ?? '';
      const day = contractValidityString.substring(6, 8);
      const month = contractValidityString.substring(4, 6);
      const year = contractValidityString.substring(0, 4);
      this.contractValidityDate.set(day + '/' + month + '/' + year);
    }
  }

  getSystemTypeLabel(value: string | null | undefined): string {
    return value ? mapSystemTypeLabel(value) : '-';
  }

  getFunctionLabel(value: string | null | undefined): string {
    return value ? mapTaskFunctionLabel(value) : '-';
  }

  getRiskLevelLabel(value: string | null | undefined): string {
    return mapRiskLevelLabel(value) || '-';
  }

  get activeStage(): SystemReviewStage | undefined {
    if (this.isCorrectionState() && this.canCurrentUserViewReview()) {
      return 'chief';
    }

    if (this.systemDetail()?.state?.toUpperCase() !== 'PENDING') {
      return undefined;
    }

    return this.stateGroups.find((stage) => stage.state === 'PENDING')?.group;
  }

  get reviewStatesButtonLabel(): string {
    if (!this.canCurrentUserReview()) {
      return 'Consultar revisión';
    }

    return 'Revisar sistema de IA';
  }

  get reviewButtonLabel(): string {
    if (!this.canCurrentUserReview()) {
      return 'Consultar revisión';
    }

    return 'Revisar sistema de IA';
  }

  private setStateInfo(state?: string | null): void {
    const stateInfo = mapSystemStateInfo(state, 'development');

    this.stateTextFormat.set(stateInfo.text);
    this.colorBox.set(stateInfo.colorClass);
  }

  isCorrectionState(): boolean {
    return this.systemDetail()?.state?.toUpperCase() === 'CORRECTION';
  }

  canDeploySystem(): boolean {
    return this.systemDetail()?.state?.toUpperCase() === 'APPROVED';
  }

  canCurrentUserReview(): boolean {
    const detail = this.systemDetail();

    if (!this.stateGroups.length || !detail) {
      return false;
    }

    if (detail.state?.toUpperCase() !== 'PENDING') {
      return false;
    }

    const activeStage = this.activeStage;

    if (!activeStage) {
      return false;
    }

    switch (activeStage) {
      case 'support':
        return this.userRoles.includes('apoyo_OGIA');
      case 'reviewers':
        return this.getReviewerRolesInWorkflowOrder(this.userRoles).some(
          (role) => this.getReviewStateForUserRole(detail, role) === 'PENDING'
        );
      case 'chief':
        return this.userRoles.includes('jefe_servicio_OGIA');
      default:
        return false;
    }
  }

  canCurrentUserViewReview(): boolean {
    const detail = this.systemDetail();

    if (!detail) {
      return false;
    }

    if (this.isCorrectionState()) {
      return this.isCurrentUserReviewRegistered(detail);
    }

    if (detail.state?.toUpperCase() !== 'PENDING') {
      return false;
    }

    if (this.normalizeReviewState(detail.serviceManagerState) !== 'PENDING') {
      return false;
    }

    return this.userRoles.some((role) => {
      if (role === 'jefe_servicio_OGIA') {
        return false;
      }

      const state = this.getReviewStateForUserRole(detail, role);

      return state === 'APPROVED' || state === 'CORRECTION';
    });
  }

  canCurrentUserAccessReview(): boolean {
    return this.canCurrentUserReview() || this.canCurrentUserViewReview();
  }

  canCurrentUserCorrect(): boolean {
    const detail = this.systemDetail();

    if (!detail || !this.isCorrectionState()) {
      return false;
    }

    const systemOrganizations = this.getSystemOrganizations(detail);

    if (!systemOrganizations.length) {
      return false;
    }

    const userData = this.getStoredUserData();

    return Object.entries(userData.roles ?? {}).some(([role, orgs]) => {
      return (
        this.isSystemCorrectionRole(role) &&
        Array.isArray(orgs) &&
        orgs.some((org) => systemOrganizations.includes(this.normalizeOrgCodeForEnvironment(org)))
      );
    });
  }

  goToSystemReview(): void {
    if (!this.id_system || !this.canCurrentUserAccessReview()) {
      return;
    }

    this.router.navigate(['revision'], { relativeTo: this.route });
  }

  goToCorrection(): void {
    if (!this.id_system || !this.canCurrentUserCorrect()) {
      return;
    }

    this.router.navigate(['/catalogo-ia', 'sistemas-de-ia', 'detalles', this.id_system, 'subsanar']);
  }

  private buildStateGroups(detail: SystemReviewDetail): { group: SystemReviewStage; state: string }[] {
    return [
      { group: 'support', state: this.normalizeReviewState(detail.supportGroupState) },
      { group: 'reviewers', state: this.buildReviewersState(detail) },
      { group: 'chief', state: this.normalizeReviewState(detail.serviceManagerState) },
    ];
  }

  private buildReviewersState(detail: SystemReviewDetail): string {
    const states = [
      detail.technicalReviewerState,
      detail.legalReviewerState,
      detail.functionalReviewerState,
      detail.dpdReviewerState,
    ].map((state) => this.normalizeReviewState(state));

    return states.every((state) => state !== 'PENDING') ? 'APPROVED' : 'PENDING';
  }

  private normalizeReviewState(state?: string | null): string {
    return (state ?? 'PENDING').toUpperCase();
  }

  private getUserRolesForSystem(detail: SystemReviewDetail): SystemReviewRole[] {
    const userData = this.getStoredUserData();
    const systemOrganizations = this.getSystemOrganizations(detail);

    if (!systemOrganizations.length) {
      return [];
    }

    return Object.entries(userData.roles ?? {})
      .filter(
        ([, orgs]) =>
          Array.isArray(orgs) &&
          orgs.some((org) => systemOrganizations.includes(this.normalizeOrgCodeForEnvironment(org)))
      )
      .map(([role]) => role)
      .filter((role): role is SystemReviewRole => this.isSystemReviewRole(role));
  }

  private getReviewerRolesInWorkflowOrder(userRoles: SystemReviewRole[]): SystemReviewRole[] {
    const reviewerRoles: SystemReviewRole[] = ['funcional_OGIA', 'juridico_OGIA', 'tecnico_OGIA', 'dpd_OGIA'];

    return reviewerRoles.filter((role) => userRoles.includes(role));
  }

  private getReviewStateForUserRole(detail: SystemReviewDetail, role: SystemReviewRole): string {
    const stateByRole: Record<SystemReviewRole, string | null | undefined> = {
      apoyo_OGIA: detail.supportGroupState,
      funcional_OGIA: detail.functionalReviewerState,
      juridico_OGIA: detail.legalReviewerState,
      tecnico_OGIA: detail.technicalReviewerState,
      dpd_OGIA: detail.dpdReviewerState,
      jefe_servicio_OGIA: detail.serviceManagerState,
    };

    return this.normalizeReviewState(stateByRole[role]);
  }

  private isCurrentUserReviewRegistered(detail: SystemReviewDetail): boolean {
    return this.userRoles.some((role) => {
      const state = this.getReviewStateForUserRole(detail, role);

      return state === 'APPROVED' || state === 'CORRECTION';
    });
  }

  private normalizeOrgCodeForEnvironment(orgCode: string): string {
    return orgCode.startsWith(environment.environment) ? orgCode : `${environment.environment}${orgCode}`;
  }

  private getSystemOrganizations(detail: SystemReviewDetail): string[] {
    return [detail.codigo_organismo, detail.orgCodeCreator]
      .filter((org): org is string => !!org)
      .map((org) => this.normalizeOrgCodeForEnvironment(org));
  }

  private getStoredUserData(): SystemReviewUserData {
    const storedUserData = localStorage.getItem('userData');

    if (!storedUserData) {
      return {};
    }

    return JSON.parse(storedUserData) as SystemReviewUserData;
  }

  private isSystemReviewRole(role: string): role is SystemReviewRole {
    return (
      role === 'apoyo_OGIA' ||
      role === 'funcional_OGIA' ||
      role === 'juridico_OGIA' ||
      role === 'tecnico_OGIA' ||
      role === 'dpd_OGIA' ||
      role === 'jefe_servicio_OGIA'
    );
  }

  private isSystemCorrectionRole(role: string): role is SystemCorrectionRole {
    return role === 'responsable_IA' || role === 'jefe_consumidor';
  }
}
